<template>
  <AdminLayout page-title="Knowledge Hub" page-subtitle="Manage and upload oilfield knowledge articles">
    <div class="space-y-8 pt-8">
      <!-- Page Header -->
      <div class="mb-8">
        <div class="flex items-center justify-between mb-6">
          <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium">
            <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
            Admin Knowledge Hub
          </div>
          <button 
            @click="loadKnowledgeData" 
            :disabled="isLoading"
            class="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white text-sm font-medium rounded-xl hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg v-if="isLoading" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
            <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
            <span>{{ isLoading ? 'Refreshing...' : 'Refresh' }}</span>
          </button>
        </div>
        <h1 class="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
          Knowledge Management
        </h1>
        <p class="text-xl text-slate-300 mb-8 max-w-2xl">
          Upload and manage comprehensive oilfield knowledge articles for North Dakota operations.
        </p>
        <div v-if="lastUpdated" class="text-sm text-slate-400">
          Last updated: {{ new Intl.DateTimeFormat('en-US', { 
            month: 'short', 
            day: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit' 
          }).format(lastUpdated) }}
        </div>
      </div>

      <!-- AI Knowledge Generation System -->
      <div class="bg-slate-800/40 backdrop-blur-sm rounded-3xl border border-slate-700/50 p-8">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h2 class="text-2xl font-bold text-white mb-2">AI Knowledge Generator</h2>
            <p class="text-slate-300">Generate comprehensive articles about North Dakota oilfield operations</p>
          </div>
          <div class="flex items-center space-x-2">
            <div class="w-3 h-3 bg-emerald-400 rounded-full animate-pulse"></div>
            <span class="text-emerald-400 text-sm font-medium">AI System Active</span>
          </div>
        </div>

        <!-- AI Generation Form -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Topic Selection -->
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Topic Category</label>
              <select v-model="aiForm.category" class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all">
                <option value="">Select a category</option>
                <option value="safety">Safety & Compliance</option>
                <option value="operations">Field Operations</option>
                <option value="equipment">Equipment & Technology</option>
                <option value="regulations">Regulations & Permits</option>
                <option value="environmental">Environmental Management</option>
                <option value="training">Training & Certification</option>
                <option value="economics">Economic Impact</option>
                <option value="technology">Emerging Technologies</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Specific Topic</label>
              <input 
                v-model="aiForm.topic" 
                type="text" 
                placeholder="e.g., Fracking regulations in North Dakota, Well completion techniques"
                class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
              >
            </div>

            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Article Length</label>
              <select v-model="aiForm.length" class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all">
                <option value="short">Short (500-800 words)</option>
                <option value="medium">Medium (800-1200 words)</option>
                <option value="long">Long (1200-2000 words)</option>
                <option value="comprehensive">Comprehensive (2000+ words)</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Target Audience</label>
              <select v-model="aiForm.audience" class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all">
                <option value="operators">Field Operators</option>
                <option value="managers">Managers & Supervisors</option>
                <option value="engineers">Engineers & Technicians</option>
                <option value="safety">Safety Personnel</option>
                <option value="general">General Workforce</option>
              </select>
            </div>
          </div>

          <!-- AI Settings -->
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Research Sources</label>
              <div class="space-y-2">
                <label class="flex items-center space-x-3">
                  <input type="checkbox" v-model="aiForm.sources.industryReports" class="w-4 h-4 text-emerald-500 bg-slate-700 border-slate-600 rounded focus:ring-emerald-500">
                  <span class="text-slate-300">Industry Reports</span>
                </label>
                <label class="flex items-center space-x-3">
                  <input type="checkbox" v-model="aiForm.sources.regulatoryDocs" class="w-4 h-4 text-emerald-500 bg-slate-700 border-slate-600 rounded focus:ring-emerald-500">
                  <span class="text-slate-300">Regulatory Documents</span>
                </label>
                <label class="flex items-center space-x-3">
                  <input type="checkbox" v-model="aiForm.sources.newsArticles" class="w-4 h-4 text-emerald-500 bg-slate-700 border-slate-600 rounded focus:ring-emerald-500">
                  <span class="text-slate-300">News Articles</span>
                </label>
                <label class="flex items-center space-x-3">
                  <input type="checkbox" v-model="aiForm.sources.academicPapers" class="w-4 h-4 text-emerald-500 bg-slate-700 border-slate-600 rounded focus:ring-emerald-500">
                  <span class="text-slate-300">Academic Papers</span>
                </label>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Include Local Context</label>
              <div class="space-y-2">
                <label class="flex items-center space-x-3">
                  <input type="checkbox" v-model="aiForm.localContext.northDakota" class="w-4 h-4 text-emerald-500 bg-slate-700 border-slate-600 rounded focus:ring-emerald-500">
                  <span class="text-slate-300">North Dakota specific regulations</span>
                </label>
                <label class="flex items-center space-x-3">
                  <input type="checkbox" v-model="aiForm.localContext.bakken" class="w-4 h-4 text-emerald-500 bg-slate-700 border-slate-600 rounded focus:ring-emerald-500">
                  <span class="text-slate-300">Bakken Formation details</span>
                </label>
                <label class="flex items-center space-x-3">
                  <input type="checkbox" v-model="aiForm.localContext.localCompanies" class="w-4 h-4 text-emerald-500 bg-slate-700 border-slate-600 rounded focus:ring-emerald-500">
                  <span class="text-slate-300">Local company examples</span>
                </label>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Additional Instructions</label>
              <textarea 
                v-model="aiForm.instructions" 
                rows="3"
                placeholder="Any specific requirements or focus areas..."
                class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all resize-none"
              ></textarea>
            </div>
          </div>
        </div>

        <!-- Generate Button -->
        <div class="mt-6 flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <button 
              @click="generateArticle" 
              :disabled="isGenerating || !canGenerate"
              class="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-medium rounded-xl hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg v-if="isGenerating" class="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
              </svg>
              <span>{{ isGenerating ? 'Generating Article...' : 'Generate Article' }}</span>
            </button>
            
            <button 
              @click="generateMultiple" 
              :disabled="isGenerating || !canGenerate"
              class="flex items-center space-x-2 px-4 py-3 border border-slate-600 text-slate-300 font-medium rounded-xl hover:bg-slate-700/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
              </svg>
              <span>Batch Generate</span>
            </button>
          </div>

          <div class="text-sm text-slate-400">
            <span class="font-medium text-emerald-400">{{ aiStats.articlesGenerated }}</span> articles generated this month
          </div>
        </div>
      </div>

      <!-- Knowledge Hub Overview -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div class="knowledge-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Total Articles</p>
              <div v-if="isLoading" class="space-y-2">
                <div class="h-8 bg-slate-700/50 rounded w-20 animate-pulse"></div>
                <div class="h-4 bg-slate-700/50 rounded w-24 animate-pulse"></div>
              </div>
              <div v-else>
                <p class="text-3xl font-bold text-white">{{ knowledgeStats.totalArticles }}</p>
                <p class="text-sm text-emerald-400 mt-2">+{{ knowledgeStats.newArticles }} this month</p>
              </div>
            </div>
            <div class="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <img src="/src/assets/images/icons/books.png" alt="Articles" class="w-6 h-6">
            </div>
          </div>
        </div>

        <div class="knowledge-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">AI Generated</p>
              <div v-if="isLoading" class="space-y-2">
                <div class="h-8 bg-slate-700/50 rounded w-16 animate-pulse"></div>
                <div class="h-4 bg-slate-700/50 rounded w-20 animate-pulse"></div>
              </div>
              <div v-else>
                <p class="text-3xl font-bold text-white">{{ knowledgeStats.aiGenerated }}</p>
                <p class="text-sm text-emerald-400 mt-2">{{ knowledgeStats.aiPercentage }}% of total</p>
              </div>
            </div>
            <div class="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center">
              <img src="/src/assets/images/icons/ai-powered financial research tools.png" alt="AI Generated" class="w-6 h-6">
            </div>
          </div>
        </div>

        <div class="knowledge-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Total Views</p>
              <div v-if="isLoading" class="space-y-2">
                <div class="h-8 bg-slate-700/50 rounded w-20 animate-pulse"></div>
                <div class="h-4 bg-slate-700/50 rounded w-24 animate-pulse"></div>
              </div>
              <div v-else>
                <p class="text-3xl font-bold text-white">{{ knowledgeStats.totalViews }}</p>
                <p class="text-sm text-emerald-400 mt-2">+{{ knowledgeStats.newViews }} this week</p>
              </div>
            </div>
            <div class="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
              <img src="/src/assets/images/icons/growth chart.png" alt="Views" class="w-6 h-6">
            </div>
          </div>
        </div>

        <div class="knowledge-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Pending Review</p>
              <div v-if="isLoading" class="space-y-2">
                <div class="h-8 bg-slate-700/50 rounded w-16 animate-pulse"></div>
                <div class="h-4 bg-slate-700/50 rounded w-20 animate-pulse"></div>
              </div>
              <div v-else>
                <p class="text-3xl font-bold text-white">{{ knowledgeStats.pendingReview }}</p>
                <p class="text-sm text-amber-400 mt-2">Awaiting approval</p>
              </div>
            </div>
            <div class="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
              <img src="/src/assets/images/icons/checklist and pen.png" alt="Pending" class="w-6 h-6">
            </div>
          </div>
        </div>
      </div>

      <!-- Article Management -->
      <div class="bg-slate-800/40 backdrop-blur-sm rounded-3xl border border-slate-700/50 p-8">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h2 class="text-2xl font-bold text-white mb-2">Article Management</h2>
            <p class="text-slate-300">Review, edit, and publish knowledge articles</p>
          </div>
          <div class="flex items-center space-x-3">
            <button @click="showUploadModal = true" class="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-medium rounded-xl hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
              </svg>
              <span>Upload Article</span>
            </button>
          </div>
        </div>

        <!-- Article Filters -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Category</label>
            <select v-model="filters.category" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all">
              <option value="">All Categories</option>
              <option value="safety">Safety & Compliance</option>
              <option value="operations">Field Operations</option>
              <option value="equipment">Equipment & Technology</option>
              <option value="regulations">Regulations & Permits</option>
              <option value="environmental">Environmental Management</option>
              <option value="training">Training & Certification</option>
              <option value="economics">Economic Impact</option>
              <option value="technology">Emerging Technologies</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Status</label>
            <select v-model="filters.status" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all">
              <option value="">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="pending">Pending Review</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Source</label>
            <select v-model="filters.source" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all">
              <option value="">All Sources</option>
              <option value="ai">AI Generated</option>
              <option value="manual">Manual Upload</option>
              <option value="external">External Source</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Search</label>
            <input 
              v-model="filters.search" 
              type="text" 
              placeholder="Search articles..."
              class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
            >
          </div>
        </div>

        <!-- Articles Table -->
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-slate-700/30">
              <tr>
                <th class="px-4 py-3 text-left text-sm font-semibold text-slate-300">Title</th>
                <th class="px-4 py-3 text-left text-sm font-semibold text-slate-300">Category</th>
                <th class="px-4 py-3 text-left text-sm font-semibold text-slate-300">Status</th>
                <th class="px-4 py-3 text-left text-sm font-semibold text-slate-300">Source</th>
                <th class="px-4 py-3 text-left text-sm font-semibold text-slate-300">Views</th>
                <th class="px-4 py-3 text-left text-sm font-semibold text-slate-300">Created</th>
                <th class="px-4 py-3 text-left text-sm font-semibold text-slate-300">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-700/50">
              <tr v-for="article in filteredArticles" :key="article.id" class="hover:bg-slate-700/20 transition-colors">
                <td class="px-4 py-3">
                  <div>
                    <p class="text-white font-medium">{{ article.title }}</p>
                    <p class="text-sm text-slate-400">{{ article.excerpt }}</p>
                  </div>
                </td>
                <td class="px-4 py-3">
                  <span :class="getCategoryBadgeClass(article.category)" class="px-2 py-1 rounded-full text-xs font-medium">
                    {{ article.category }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <span :class="getStatusBadgeClass(article.status)" class="px-2 py-1 rounded-full text-xs font-medium">
                    {{ article.status }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <span :class="getSourceBadgeClass(article.source)" class="px-2 py-1 rounded-full text-xs font-medium">
                    {{ article.source }}
                  </span>
                </td>
                <td class="px-4 py-3 text-slate-300">{{ article.views }}</td>
                <td class="px-4 py-3 text-slate-300">{{ formatDate(article.createdAt) }}</td>
                <td class="px-4 py-3">
                  <div class="flex items-center space-x-2">
                    <button @click="editArticle(article)" class="text-emerald-400 hover:text-emerald-300">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                      </svg>
                    </button>
                    <button @click="viewArticle(article)" class="text-cyan-400 hover:text-cyan-300">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                      </svg>
                    </button>
                    <button @click="deleteArticle(article.id)" class="text-red-400 hover:text-red-300">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAdminStore } from '../../stores/adminStore'
import AdminLayout from '../admin/AdminLayout.vue'

const router = useRouter()
const adminStore = useAdminStore()

// State
const isLoading = ref(false)
const isGenerating = ref(false)
const lastUpdated = ref<Date | null>(null)
const showUploadModal = ref(false)

// AI Form
const aiForm = reactive({
  category: '',
  topic: '',
  length: 'medium',
  audience: 'operators',
  sources: {
    industryReports: true,
    regulatoryDocs: true,
    newsArticles: true,
    academicPapers: false
  },
  localContext: {
    northDakota: true,
    bakken: true,
    localCompanies: true
  },
  instructions: ''
})

// Filters
const filters = reactive({
  category: '',
  status: '',
  source: '',
  search: ''
})

// Stats
const knowledgeStats = reactive({
  totalArticles: 0,
  newArticles: 0,
  aiGenerated: 0,
  aiPercentage: 0,
  totalViews: 0,
  newViews: 0,
  pendingReview: 0
})

const aiStats = reactive({
  articlesGenerated: 0
})

// Articles
const articles = ref<any[]>([])

// Computed
const canGenerate = computed(() => {
  return aiForm.category && aiForm.topic
})

const filteredArticles = computed(() => {
  return articles.value.filter(article => {
    if (filters.category && article.category !== filters.category) return false
    if (filters.status && article.status !== filters.status) return false
    if (filters.source && article.source !== filters.source) return false
    if (filters.search && !article.title.toLowerCase().includes(filters.search.toLowerCase())) return false
    return true
  })
})

// Methods
const loadKnowledgeData = async () => {
  try {
    isLoading.value = true
    
    const response = await fetch('/api/admin/knowledge/stats', {
      headers: {
        'Authorization': `Bearer ${adminStore.adminToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      if (data.stats) Object.assign(knowledgeStats, data.stats)
      if (data.aiStats) Object.assign(aiStats, data.aiStats)
      if (typeof data.articles === 'number') {
        knowledgeStats.totalArticles = data.articles
      } else if (Array.isArray(data.articles)) {
        articles.value = data.articles
      }
      if (typeof data.views === 'number') knowledgeStats.totalViews = data.views
      lastUpdated.value = new Date()
    }
  } catch (error) {
    console.error('Error loading knowledge data:', error)
    // Load mock data for demo
    loadMockData()
  } finally {
    isLoading.value = false
  }
}

const loadMockData = () => {
  Object.assign(knowledgeStats, {
    totalArticles: 156,
    newArticles: 23,
    aiGenerated: 89,
    aiPercentage: 57,
    totalViews: 1247,
    newViews: 156,
    pendingReview: 8
  })
  
  Object.assign(aiStats, {
    articlesGenerated: 45
  })
  
  articles.value = [
    {
      id: 1,
      title: 'North Dakota Fracking Regulations: A Comprehensive Guide',
      excerpt: 'Complete overview of current fracking regulations in North Dakota...',
      category: 'regulations',
      status: 'published',
      source: 'ai',
      views: 234,
      createdAt: '2024-12-01T10:30:00Z'
    },
    {
      id: 2,
      title: 'Bakken Formation Well Completion Techniques',
      excerpt: 'Advanced well completion methods specific to the Bakken formation...',
      category: 'operations',
      status: 'published',
      source: 'ai',
      views: 189,
      createdAt: '2024-11-28T14:15:00Z'
    }
  ]
}

const generateArticle = async () => {
  try {
    isGenerating.value = true
    
    const response = await fetch('/api/admin/knowledge/generate', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${adminStore.adminToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(aiForm)
    })
    
    if (response.ok) {
      const result = await response.json()
      // Add the generated article to the list
      articles.value.unshift(result.article)
      knowledgeStats.totalArticles++
      knowledgeStats.aiGenerated++
      aiStats.articlesGenerated++
      
      // Reset form
      Object.assign(aiForm, {
        category: '',
        topic: '',
        length: 'medium',
        audience: 'operators',
        sources: {
          industryReports: true,
          regulatoryDocs: true,
          newsArticles: true,
          academicPapers: false
        },
        localContext: {
          northDakota: true,
          bakken: true,
          localCompanies: true
        },
        instructions: ''
      })
    }
  } catch (error) {
    console.error('Error generating article:', error)
  } finally {
    isGenerating.value = false
  }
}

const generateMultiple = async () => {
  // Generate multiple articles based on common topics
  const topics = [
    'Safety protocols for North Dakota oilfield operations',
    'Environmental compliance in the Bakken region',
    'Equipment maintenance best practices',
    'Worker training and certification requirements'
  ]
  
  for (const topic of topics) {
    aiForm.topic = topic
    await generateArticle()
    // Add delay between generations
    await new Promise(resolve => setTimeout(resolve, 2000))
  }
}

const editArticle = (article: any) => {
  router.push(`/admin/knowledge/edit/${article.id}`)
}

const viewArticle = (article: any) => {
  router.push(`/admin/knowledge/view/${article.id}`)
}

const deleteArticle = async (articleId: number) => {
  if (!confirm('Are you sure you want to delete this article?')) return
  
  try {
    const response = await fetch(`/api/admin/knowledge/${articleId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${adminStore.adminToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      articles.value = articles.value.filter(a => a.id !== articleId)
      knowledgeStats.totalArticles--
    }
  } catch (error) {
    console.error('Error deleting article:', error)
  }
}

const getCategoryBadgeClass = (category: string) => {
  const classes = {
    safety: 'bg-red-500/20 text-red-400 border border-red-500/30',
    operations: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
    equipment: 'bg-purple-500/20 text-purple-400 border border-purple-500/30',
    regulations: 'bg-amber-500/20 text-amber-400 border border-amber-500/30',
    environmental: 'bg-green-500/20 text-green-400 border border-green-500/30',
    training: 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30',
    economics: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
    technology: 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30'
  }
  return classes[category as keyof typeof classes] || 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
}

const getStatusBadgeClass = (status: string) => {
  const classes = {
    published: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
    draft: 'bg-slate-500/20 text-slate-400 border border-slate-500/30',
    pending: 'bg-amber-500/20 text-amber-400 border border-amber-500/30',
    archived: 'bg-red-500/20 text-red-400 border border-red-500/30'
  }
  return classes[status as keyof typeof classes] || 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
}

const getSourceBadgeClass = (source: string) => {
  const classes = {
    ai: 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30',
    manual: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
    external: 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
  }
  return classes[source as keyof typeof classes] || 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

// Lifecycle
onMounted(() => {
  loadKnowledgeData()
})
</script>

<style scoped>
.knowledge-stat-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-3xl border border-slate-700/50 p-6 transition-all duration-200 hover:bg-slate-700/40 hover:border-slate-600/50;
}
</style>
